import { inject, Injectable } from "@angular/core";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { FlexContainer } from "@pdf/helpers/container/image-container/flex-container";
import { FlexContainerService } from "@pdf/helpers/container/image-container/flex-container.service";
import { DraegerFonts } from "@pdf/helpers/draeger-fonts";
import { ContainerProperties, FontNames, Headlines, PdfProperties, TableProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { catchError, concatMap, filter, finalize, from, last, map, Observable, of, switchMap, tap, toArray } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PdfFloorplanImageService {
  private containerService = inject(FlexContainerService);
  private projectImageService = inject(ProjectImageService);

  private headlines = new Headlines();
  private readonly MAX_WIDTH = 3241;
  private readonly MAX_HEIGHT = 1975 - 2 * TableProperties.PADDING_32;
  private readonly MAX_CONTAINER_WIDTH = this.MAX_WIDTH * 0.75;

  generate(pdf: jsPDF, project: ScopedProject): Observable<jsPDF> {
    return new Observable((observer) => {
      pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      this.addFonts(pdf);
      if (project.cachedFloorplans.length === 0) {
        finalize(() => {
          observer.next(pdf);
          observer.complete();
        });
      }
      from(project.cachedFloorplans)
        .pipe(
          concatMap((floorplan) => from(floorplan.images)),
          concatMap((floorplanImage) =>
            this.addImage(pdf, floorplanImage, project).pipe(
              map((containerWidth) => ({ floorplanImage, containerWidth })),
              filter(({ containerWidth }) => containerWidth !== undefined),
            ),
          ),
          concatMap(({ floorplanImage, containerWidth }) =>
            containerWidth && floorplanImage.notes ? this.addNotes(pdf, floorplanImage.notes, containerWidth) : of(null),
          ),
          finalize(() => {
            observer.next(pdf);
            observer.complete();
          }),
        )
        .subscribe();
    });
  }

  generateMonitoring(pdf: jsPDF, project: ScopedProject): Observable<jsPDF> {
    this.addFonts(pdf);

    pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);

    return this.projectImageService.getAllImageMetadataFromIndexedDB(project.id).pipe(
      switchMap((allImages: ProjectImageMetadata[]) => {
        if (allImages.length === 0) {
          return of(pdf);
        }

        const IMAGES_PER_PAGE = 4;

        const pages: ProjectImageMetadata[][] = [];
        for (let i = 0; i < allImages.length; i += IMAGES_PER_PAGE) {
          pages.push(allImages.slice(i, i + IMAGES_PER_PAGE));
        }

        return from(pages).pipe(
          concatMap((pageImages) => this.addMonitoringImagePage(pdf, pageImages, project)),
          toArray(),
          map(() => pdf),
        );
      }),
    );
  }

  private addMonitoringImagePage(pdf: jsPDF, images: ProjectImageMetadata[], project: ScopedProject): Observable<void> {
    pdf.addPage();

    const COLS = 2;
    const TILE_GAP = 60;
    const TOTAL_WIDTH = this.MAX_WIDTH;
    const TILE_WIDTH = Math.floor((TOTAL_WIDTH - TILE_GAP) / COLS);
    const TITLE_SPACE = Math.floor(((ContainerProperties.Y - PdfProperties.HEADLINE_Y) * 2) / 3);
    const TILE_HEIGHT = Math.floor(
      (ContainerProperties.Y + ContainerProperties.HEIGHT - PdfProperties.HEADLINE_Y - TILE_GAP) / 2,
    );
    const BOX_HEIGHT = TILE_HEIGHT - TITLE_SPACE;
    const PADDING = 24;

    return from(images).pipe(
      concatMap((image, index) => {
        const col = index % COLS;
        const row = Math.floor(index / COLS);
        const tileX = ContainerProperties.LEFT_X + col * (TILE_WIDTH + TILE_GAP);
        const tileY = PdfProperties.HEADLINE_Y + row * (TILE_HEIGHT + TILE_GAP);
        return this.addMonitoringTile(pdf, image, tileX, tileY, TILE_WIDTH, BOX_HEIGHT, TITLE_SPACE, PADDING, project);
      }),
      last(),
    );
  }

  private addMonitoringTile(
    pdf: jsPDF,
    projectImage: ProjectImageMetadata,
    tileX: number,
    tileY: number,
    tileWidth: number,
    boxHeight: number,
    titleSpace: number,
    padding: number,
    project: ScopedProject,
  ): Observable<void> {
    pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
    pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);
    pdf.text(projectImage.name, tileX, tileY, { baseline: "top" });

    const boxY = tileY + titleSpace;
    pdf.setLineWidth(PdfProperties.BORDER_WIDTH);
    pdf.setDrawColor(PdfProperties.BORDER_COLOR);
    pdf.setFillColor(PdfProperties.BACKGROUND_COLOR);

    let imageBoxHeight = boxHeight;

    if (projectImage.description) {
      pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");
      pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
      const lines: string[] = pdf.splitTextToSize(projectImage.description, tileWidth - 2 * padding);
      const DESC_PADDING_BOTTOM = 41;
      const descHeight = lines.length * TableProperties.TEXT_HEIGHT + padding + DESC_PADDING_BOTTOM;
      imageBoxHeight = boxHeight - descHeight;

      // Draw outer box covering both sections, then a single separator line
      pdf.rect(tileX, boxY, tileWidth, boxHeight);
      pdf.line(tileX, boxY + imageBoxHeight, tileX + tileWidth, boxY + imageBoxHeight);

      pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT, "normal");
      pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      pdf.text(lines, tileX + padding, boxY + imageBoxHeight + padding, { baseline: "top" });
    } else {
      pdf.rect(tileX, boxY, tileWidth, boxHeight);
    }

    return this.loadImageData(project, projectImage.id).pipe(
      switchMap((imageDataUrl) =>
        this.getImageDimensions(imageDataUrl).pipe(map((dimensions) => ({ imageDataUrl, dimensions }))),
      ),
      tap(({ imageDataUrl, dimensions }) => {
        const maxImgWidth = tileWidth - 2 * padding;
        const maxImgHeight = imageBoxHeight - 2 * padding;

        const ratio = Math.min(maxImgWidth / dimensions.width, maxImgHeight / dimensions.height, 1);

        const imgWidth = dimensions.width * ratio;
        const imgHeight = dimensions.height * ratio;

        const imgX = tileX + padding + (maxImgWidth - imgWidth) / 2;
        const imgY = boxY + padding + (maxImgHeight - imgHeight) / 2;

        const format = imageDataUrl.includes("image/png") ? "PNG" : "JPEG";

        pdf.addImage(imageDataUrl, format, imgX, imgY, imgWidth, imgHeight);
      }),
      map(() => undefined),
      catchError((error) => {
        console.error("Error adding image to monitoring PDF tile:", error);
        return of(undefined);
      }),
    );
  }

  private addFonts(doc: jsPDF) {
    new DraegerFonts().fonts.forEach((font) => {
      doc.addFileToVFS(font.ttfName, font.base64);
      doc.addFont(font.ttfName, font.fontName, font.fontWeight);
    });
  }

  addImage(pdf: jsPDF, floorplanImage: FloorplanImage, project: ScopedProject): Observable<number | undefined> {
    return this.projectImageService.getImageMetadataFromIndexedDB(floorplanImage.projectImageId).pipe(
      switchMap((imageMetadata) =>
        this.loadImageData(project, floorplanImage.projectImageId).pipe(
          switchMap((imageDataUrl) =>
            this.getImageDimensions(imageDataUrl).pipe(map((dimensions) => ({ imageDataUrl, dimensions }))),
          ),
          switchMap(({ imageDataUrl, dimensions }) => {
            const useMaxWidth = !floorplanImage.notes;

            const [fittedWidth, fittedHeight] = this.fitImageToFrame(dimensions, useMaxWidth);
            const containerWidth = this.calculateContainerWidth(fittedWidth, useMaxWidth);

            const container = new FlexContainer(ContainerProperties.LEFT_X, containerWidth);
            const pageHeadline = imageMetadata?.name ?? "";

            this.containerService.add(pdf, container, pageHeadline, true, true);

            container.xPosition += TableProperties.PADDING_32;
            container.yPosition += TableProperties.PADDING_32;

            pdf.setFont(FontNames.DRAEGER_PANGEA);

            const format = imageDataUrl.includes("image/png") ? "PNG" : "JPEG";

            pdf.addImage(imageDataUrl, format, container.xPosition, container.yPosition, fittedWidth, fittedHeight);

            return of(containerWidth);
          }),
        ),
      ),
      catchError((error) => {
        console.error("Error adding image to PDF:", error);
        return of(undefined);
      }),
    );
  }

  private calculateContainerWidth(fittedWidth: number, useMaxWidth: boolean) {
    const containerWidth = fittedWidth + 2 * TableProperties.PADDING_32;
    return useMaxWidth ? this.MAX_WIDTH : containerWidth;
  }

  private getImageDimensions(imageUrl: string): Observable<{ width: number; height: number }> {
    return new Observable((observer) => {
      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        observer.next({ width: img.width, height: img.height });
        observer.complete();
      };

      img.onerror = () => {
        observer.error(new Error("Failed to load image"));
      };
    });
  }

  private fitImageToFrame(dimensions: { width: number; height: number }, useMaxWidth: boolean) {
    const maxWidth = useMaxWidth ? this.MAX_WIDTH : this.MAX_CONTAINER_WIDTH;
    let ratio: number = Math.min(maxWidth / dimensions.width, this.MAX_HEIGHT / dimensions.height);
    ratio = ratio > 1 ? 1 : ratio;
    return [dimensions.width * ratio, dimensions.height * ratio];
  }

  private addNotes(pdf: jsPDF, notes: string, imageContainerWidth: number): Observable<any> {
    let xPosition: number = this.calculateNotesContainerPosition(imageContainerWidth);
    const notesContainerWidth: number = this.MAX_WIDTH - xPosition + ContainerProperties.LEFT_X;
    const container = new FlexContainer(xPosition, notesContainerWidth);
    let yPosition = container.yPosition + TableProperties.HEADLINE_OFFSET_Y;
    container.addFrame(pdf);
    xPosition += TableProperties.PADDING_32;

    this.addNotesHeadline(pdf, xPosition, yPosition);

    yPosition += TableProperties.PADDING_48;
    this.addNotesText(pdf, xPosition, yPosition, container.width, notes);
    return of();
  }
  private addNotesText(pdf: jsPDF, xPosition: number, yPosition: number, containerWidth: number, notes: string) {
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    const text = pdf.splitTextToSize(notes, containerWidth - 2 * TableProperties.PADDING_32);
    pdf.text(text, xPosition, yPosition, { baseline: "top" });
  }

  private addNotesHeadline(pdf: jsPDF, xPosition: number, yPosition: number) {
    pdf.setFontSize(PdfProperties.TABLE_OF_CONTENTS_FONT_SIZE_LARGE);
    pdf.text(this.headlines.NOTES, xPosition, yPosition, { baseline: "top" });
  }

  private calculateNotesContainerPosition(containerWidth: number) {
    return ContainerProperties.LEFT_X + containerWidth + TableProperties.PADDING_64;
  }

  private loadImageData(project: ScopedProject, imageId: string): Observable<string> {
    return this.projectImageService.getImageSyncStatus(imageId).pipe(
      switchMap((syncState) => {
        if (syncState === SyncState.NEW) {
          return this.projectImageService
            .getOriginalImageFromIndexedDB(imageId)
            .pipe(switchMap((blob: Blob) => this.blobToDataURL(blob)));
        }

        const canLoadFromCloud = project.cloudId && navigator.onLine && syncState !== SyncState.DELETED;

        const source$ = canLoadFromCloud
          ? this.projectImageService.loadImage(project.cloudId!, imageId)
          : this.projectImageService.getPreviewImageFromIndexedDB(imageId);

        return source$.pipe(switchMap((blob: Blob) => this.blobToDataURL(blob)));
      }),
    );
  }

  private blobToDataURL(blob: Blob): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();

      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };

      reader.onerror = (error) => {
        observer.error(error);
      };

      reader.readAsDataURL(blob);
    });
  }
}
